import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Spinner,
  Container,
  Row,
  Col,
  Badge,
  Modal
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useUserAgreements from "hooks/useUserAgreements";
import useFormattedDate from "hooks/useFormatDate";

// Map status to badge colors
const statusColors = {
  ACTIVE: "success",
  DRAFT: "secondary",
  READY: "warning",
  EXPIRED: "danger",
  ABANDONED: "secondary",
  DELETED: "danger",
};

const UserAgreementDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAgreementById } = useUserAgreements();

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { formatDateTime } = useFormattedDate();

  // Modal states
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showActiveBlockedModal, setShowActiveBlockedModal] = useState(false);

  useEffect(() => {
    if (!id) return;

    const loadAgreement = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getAgreementById(id);
        setAgreement(data);
      } catch (err) {
        setError(err.message || "Failed to load agreement details");
      } finally {
        setLoading(false);
      }
    };

    loadAgreement();
  }, [id, getAgreementById]);

  if (loading) {
    return (
      <Container className="my-5">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 400 }}
        >
          <Spinner animation="border" variant="primary" />
          <span className="ms-3 text-sm text-primary">
            Loading agreement details...
          </span>
        </div>
      </Container>
    );
  }

  if (error || !agreement) {
    return (
      <Container className="my-5">
        <div className="text-center py-5">
          <FontAwesomeIcon
            icon="exclamation-triangle"
            size="3x"
            className="text-warning mb-3"
          />
          <h4>Agreement Not Found</h4>
          <p className="text-muted">
            {error || "The requested agreement could not be found."}
          </p>
          <Button
            variant="outline-primary"
            onClick={() => navigate("/general/user-agreements")}
          >
            Back
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <div className="my-4 px-2 px-md-0">
      {/* Top Buttons */}
      <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-2">
        <Button
          type="button"
          onClick={() => navigate("/general/user-agreements")}
          size="sm"
          variant="outline-success"
          className="d-flex align-items-center"
        >
          <FontAwesomeIcon icon="arrow-left" className="me-2" />
          <span className="d-none d-sm-inline">Back</span>
        </Button>

        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => {
            if (agreement.status === "ACTIVE") {
              setShowActiveBlockedModal(true);
              return;
            }
            setShowEditConfirm(true);
          }}
        >
          <FontAwesomeIcon icon="edit" className="me-2" />
          Edit Agreement
        </Button>
      </div>

      {/* Title */}
      <WidgetSectionTitle
        className="mb-4"
        title={`User Agreement - ${agreement.version}`}
        icon="file-contract"
        subtitle="Review the complete terms and conditions of this user agreement"
      />

      <Row className="g-3">
        {/* Agreement Content */}
        <Col xs={12} lg={8} className="order-2 order-lg-1">
          <Card className="shadow-sm">
            <Card.Header className="bg-light d-flex justify-content-between align-items-center">
              <h5 className="mb-0 fs-6 fs-md-5">Agreement Content</h5>
            </Card.Header>

            <Card.Body className="p-3 p-md-4">
              <div
                className="agreement-content"
                dangerouslySetInnerHTML={{ __html: agreement.details }}
                style={{
                  lineHeight: 1.6,
                  fontSize: "0.875rem",
                  color: "#495057",
                  maxHeight: "450px",
                  overflowY: "auto",
                  paddingRight: "10px",
                }}
              />
            </Card.Body>
          </Card>
        </Col>

        {/* Agreement Info */}
        <Col xs={12} lg={4} className="order-1 order-lg-2">
          <Card className="shadow-sm">
            <Card.Header className="bg-light">
              <h6 className="mb-0 fs-6">Other details</h6>
            </Card.Header>

            <Card.Body className="p-3">
              <div className="mb-3">
                <strong className="d-block mb-1">Agreement ID:</strong>
                <div className="text-break small">{agreement.id}</div>
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Version:</strong>
                <div>{agreement.version}</div>
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Status:</strong>
                <Badge bg={statusColors[agreement.status] || "secondary"}>
                  {agreement.status}
                </Badge>
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Created At:</strong>
                <div className="small">
                  {formatDateTime(agreement.createdAt)}
                </div>
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Updated At:</strong>
                <div className="small">
                  {formatDateTime(agreement.updatedAt)}
                </div>
              </div>

              <div className="mb-3">
                <strong className="d-block mb-1">Created By:</strong>
                <div className="text-break small">{agreement.createdBy}</div>
              </div>

              <div>
                <strong className="d-block mb-1">Updated By:</strong>
                <div className="text-break small">{agreement.updatedBy}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm Edit Modal */}
      <Modal show={showEditConfirm} onHide={() => setShowEditConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Edit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You are about to edit this agreement.
          <br />
          <strong>
            Once the agreement becomes ACTIVE, it will no longer be editable.
          </strong>
          <br /><br />
          Are you sure you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditConfirm(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowEditConfirm(false);
              navigate(`/general/user-agreements/${id}/edit`);
            }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Blocked Edit Modal for ACTIVE agreements */}
      <Modal
        show={showActiveBlockedModal}
        onHide={() => setShowActiveBlockedModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Editing Not Allowed</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          This agreement is currently <strong>ACTIVE</strong> and cannot be edited.
          <br /><br />
          Active agreements are locked to preserve the integrity of terms that users
          have already accepted.
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => setShowActiveBlockedModal(false)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserAgreementDetails;
